import { api } from "../api-client.js";
import { bindDatePickerClick } from "../utils/date-input.js";

type LoginHistoryAction = "LOGIN_SUCCESS" | "LOGIN_FAIL" | "LOGIN_LOCKED_OUT" | "LOGOUT";

interface LoginHistoryEntry {
  at: string;
  action: LoginHistoryAction;
  username: string;
  ip: string | null;
  userAgent: string | null;
}

interface LoginHistoryResult {
  items: LoginHistoryEntry[];
}

function fmtAt(iso: string): string {
  return new Date(iso).toLocaleString("ko-KR");
}

const LOGIN_ACTION_LABEL: Record<LoginHistoryAction, string> = {
  LOGIN_SUCCESS: "로그인 성공",
  LOGIN_FAIL: "로그인 실패",
  LOGIN_LOCKED_OUT: "계정 잠김",
  LOGOUT: "로그아웃",
};

// trades 테이블의 tag-buy/tag-sell(빨강=매수/파랑=매도, 국내 증권 관례)을 그대로 재사용하면
// "성공"이 빨간색으로 보여서 오히려 위험 신호처럼 읽힌다 — 여기선 실패/잠김만 경고색으로 강조하고
// 성공/로그아웃은 굳이 강조하지 않는다(기본 텍스트색).
const LOGIN_ACTION_CLASS: Record<LoginHistoryAction, string> = {
  LOGIN_SUCCESS: "",
  LOGIN_FAIL: "status-danger",
  LOGIN_LOCKED_OUT: "status-danger",
  LOGOUT: "",
};

// UA 문자열 전체를 그대로 보여주면 너무 길고 읽기 어려워서, "어디서 접속했는지"를 한눈에 보게
// OS·브라우저만 대략 뽑아낸다 — 정확한 기기 판별이 목적이 아니라 "이게 폰이었나?" 정도 감 잡는 용도.
function parseDevice(ua: string | null): string {
  if (!ua) return "—";
  let os = "기타 OS";
  if (/Android/i.test(ua)) os = "Android";
  else if (/iPhone|iPad|iPod/i.test(ua)) os = "iOS";
  else if (/Windows/i.test(ua)) os = "Windows";
  else if (/Macintosh/i.test(ua)) os = "Mac";
  else if (/Linux/i.test(ua)) os = "Linux";

  let browser = "기타 브라우저";
  if (/EdgA|Edg\//i.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/i.test(ua)) browser = "Opera";
  else if (/Chrome/i.test(ua)) browser = "Chrome";
  else if (/Firefox/i.test(ua)) browser = "Firefox";
  else if (/Safari/i.test(ua)) browser = "Safari";

  return `${os} · ${browser}`;
}

// 로그인 성공/실패/잠김/로그아웃 이력을 IP·기기 정보와 함께 조회 — 사용자 요청: "로그인한 곳과
// 접속시도한 곳의 IP 정보"를 별도 메뉴로, 날짜 범위 조회도 가능하게.
export async function renderLoginHistory(container: HTMLElement): Promise<void> {
  let from = "";
  let to = "";

  // 필터를 바꿀 때마다 화면 전체를 지웠다가 다시 그리면 응답을 기다리는 동안 깜빡였다 — 제목/필터
  // UI는 슬롯에 담아 한 번만 배치하고, 결과 영역은 새 내용이 다 준비된 뒤 한 번에 교체한다.
  container.innerHTML = `<h2 class="page-title">로그인 이력</h2>`;
  const filterRowSlot = document.createElement("div");
  const resultsSlot = document.createElement("div");
  container.append(filterRowSlot, resultsSlot);

  function renderControls(): void {
    const filterRow = document.createElement("div");
    filterRow.className = "custom-range-row";
    filterRow.innerHTML = `
      <input type="date" id="lh-from" value="${from}" />
      <span class="range-sep">~</span>
      <input type="date" id="lh-to" value="${to}" />
      <button class="btn-secondary" id="lh-apply">조회</button>
      ${from || to ? `<button class="btn-secondary" id="lh-reset">전체보기</button>` : ""}
    `;
    bindDatePickerClick(filterRow.querySelector<HTMLInputElement>("#lh-from")!);
    bindDatePickerClick(filterRow.querySelector<HTMLInputElement>("#lh-to")!);
    filterRow.querySelector<HTMLButtonElement>("#lh-apply")!.addEventListener("click", () => {
      from = filterRow.querySelector<HTMLInputElement>("#lh-from")!.value;
      to = filterRow.querySelector<HTMLInputElement>("#lh-to")!.value;
      void load();
    });
    filterRow.querySelector<HTMLButtonElement>("#lh-reset")?.addEventListener("click", () => {
      from = "";
      to = "";
      void load();
    });
    filterRowSlot.replaceChildren(filterRow);
  }

  async function load(): Promise<void> {
    renderControls();

    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const qs = params.toString();

    const card = document.createElement("div");
    card.className = "card section-gap";

    let items: LoginHistoryEntry[];
    try {
      const result = await api.get<LoginHistoryResult>(`/api/account/login-history${qs ? `?${qs}` : ""}`);
      items = result.items;
    } catch {
      card.innerHTML = `<p style="color:var(--text-dim);font-size:14px">이력을 불러오지 못했습니다.</p>`;
      resultsSlot.replaceChildren(card);
      return;
    }

    if (items.length === 0) {
      card.innerHTML = `<p style="color:var(--text-dim);font-size:14px">이력이 없습니다.</p>`;
      resultsSlot.replaceChildren(card);
      return;
    }

    const scrollWrap = document.createElement("div");
    scrollWrap.className = "table-scroll";
    const table = document.createElement("table");
    table.innerHTML = `
      <thead>
        <tr><th>시각</th><th>아이디</th><th>결과</th><th>IP</th><th>기기</th></tr>
      </thead>
      <tbody>
        ${items
          .map(
            (h) => `
          <tr>
            <td>${fmtAt(h.at)}</td>
            <td>${h.username}</td>
            <td class="${LOGIN_ACTION_CLASS[h.action]}">${LOGIN_ACTION_LABEL[h.action]}</td>
            <td>${h.ip ?? "—"}</td>
            <td>${parseDevice(h.userAgent)}</td>
          </tr>`,
          )
          .join("")}
      </tbody>
    `;
    scrollWrap.appendChild(table);
    card.appendChild(scrollWrap);
    resultsSlot.replaceChildren(card);
  }

  await load();
}
